<template>
  <div
    v-if="hasReplyContext"
    class="group flex flex-col gap-0.5 mb-1.5 px-2 py-1 rounded border-l-2 cursor-pointer transition-all duration-200 hover:bg-opacity-80"
    :class="[
  isSent
    ? 'bg-[var(--p-primary-50)]/70 dark:bg-[var(--p-primary-950)]/30 border-[var(--p-primary-500)] hover:bg-[var(--p-primary-50)] dark:hover:bg-[var(--p-primary-950)]/45'
    : 'bg-[var(--p-surface-100)] dark:bg-[var(--p-primary-800)]/20 border-[var(--p-primary-color)] hover:bg-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-800)]',
  {
    'cursor-pointer': repliedMessage,
    'cursor-default': !repliedMessage,
  },
]"
    @click="scrollToMessage"
    :title="
      repliedMessage
        ? t('whatsapp.replied_message.go_to_message', { author: authorLabel.toLowerCase() })
        : ''
    "
  >
    <div
      class="text-[10px] font-semibold flex items-center gap-1 uppercase tracking-wide"
      :class="
        isSent
          ? 'text-[var(--p-primary-700)] dark:text-[var(--p-primary-200)]'
          : 'text-[var(--p-primary-color)]'
      "
    >
      <Reply :size="10" class="flex-shrink-0" />
      <span>{{ authorLabel }}</span>
    </div>
    <div
      class="text-[11px] line-clamp-1 leading-tight"
      :class="
        isSent
          ? 'text-[var(--p-primary-900)]/80 dark:text-[var(--p-primary-100)]/80'
          : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
      "
    >
      {{ messagePreview }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, isRef, type Ref } from 'vue'
import { Reply } from 'lucide-vue-next'
import type { Message } from '../../types/interfaces'
import { MessageSourceType } from '../../types/enums'
import { useI18n } from 'vue-i18n'

interface Props {
  replyToMetaMsgId?: string
  replyToId?: string
  fromUserId?: string | null
  isSent: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()

const injectedMessages = inject<Message[] | Ref<Message[]>>('messages', [])
const messages = computed(() => (isRef(injectedMessages) ? injectedMessages.value : injectedMessages))
const hasReplyContext = computed(() => Boolean(props.replyToMetaMsgId || props.replyToId))

const repliedMessage = computed<Message | null>(() => {
  const replyIds = [props.replyToMetaMsgId, props.replyToId].filter(Boolean)
  if (!replyIds.length) return null

  return (
    messages.value.find((message) =>
      replyIds.some((replyId) => message.id === replyId || message.provider_message_id === replyId),
    ) ?? null
  )
})

const authorLabel = computed(() => {
  if (!repliedMessage.value) return t('whatsapp.replied_message.replied_message')
  return repliedMessage.value.source_type === MessageSourceType.ENTRANTE
    ? t('whatsapp.replied_message.contact')
    : t('whatsapp.replied_message.you')
})

const messagePreview = computed(() => {
  if (!repliedMessage.value) return t('whatsapp.replied_message.message_unavailable')

  const content = repliedMessage.value.content

  switch (content.type) {
    case 'text':
      return content.content || content.text || ''
    case 'image':
      return `📷 ${t('whatsapp.replied_message.image')}`
    case 'video':
      return `🎥 ${t('whatsapp.replied_message.video')}`
    case 'audio':
      return `🎵 ${t('whatsapp.replied_message.audio')}`
    case 'document':
    case 'file':
      return `📄 ${content.file_name || t('whatsapp.replied_message.document')}`
    case 'location':
      return `📍 ${t('whatsapp.replied_message.location')}`
    case 'sticker':
      return `🎨 ${t('whatsapp.replied_message.sticker')}`
    case 'contact':
      return `👤 ${t('whatsapp.replied_message.contact')}`
    case 'template':
      return `📋 ${t('whatsapp.replied_message.template')}`
    case 'quick_reply':
    case 'list_reply':
    case 'button_reply':
      return content.content || ''
    case 'interaction':
      return content.content || t('whatsapp.replied_message.interaction')
    default:
      return t('whatsapp.replied_message.message')
  }
})

const scrollToMessage = () => {
  if (repliedMessage.value) {
    const element = document.getElementById(`message-${repliedMessage.value.id}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      element.classList.add('highlight-message')
      setTimeout(() => element.classList.remove('highlight-message'), 2000)
    }
  }
}
</script>
